import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";

// const SCROLL_TEXTS = [
//   "LAUNCH FASTER",
//   "SCALE SMARTER",
//   "WIN BIGGER",
//   "STAND OUT ONLINE",
// ];

const SCROLL_TEXTS = [
  "AUTOMATE BOTTLENECKS",
  "INNOVATE INTELLIGENTLY",
  "EMBRACE DATA INTELLIGENCE",
  "SCALE WITH CONFIDENCE",
];

const pageVariants = {
  enter:  { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.5 } },
  exit:   { opacity: 0, y: -40, transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] } },
};

export default function HeroSection({ url, onExhausted, direction }) {
  const videoRef      = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [animDir,     setAnimDir]     = useState("down");

  const idxRef      = useRef(0);
  const debounceRef = useRef(false);

  // ── Stable src refs — built once on first render, never change ──────────
  // useRef stores the value without causing re-renders, and the ref
  // object identity is stable across ALL renders — unlike useMemo which
  // can technically recompute. This guarantees video.src is set exactly once.
  // const videoSrcRef = useRef(`${url}videos/bg.mp4`);
  // const gifSrc      = useRef(`${url}images/new-home/hero.gif`).current;

  // // ── Play video only after mount — avoids multiple load requests ─────────
  // useEffect(() => {
  //   const video = videoRef.current;
  //   if (!video) return;
  //   // Use ref value — stable, never changes, effect runs exactly once
  //   video.src = videoSrcRef.current;
  //   video.load();
  //   video.play().catch(() => {});
  // }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIntent = (dir) => {
    if (debounceRef.current) return;
    debounceRef.current = true;
    setTimeout(() => { debounceRef.current = false; }, 700);

    const current = idxRef.current;
    const next    = current + dir;

    if (next >= 0 && next < SCROLL_TEXTS.length) {
      idxRef.current = next;
      setScrollIndex(next);
      setAnimDir(dir > 0 ? "down" : "up");
      return;
    }

    if (dir > 0 && current === SCROLL_TEXTS.length - 1) {
      onExhausted?.();
    }
  };

  useEffect(() => {
    const onWheel = (e) => {
      // Let the chatbot scroll its own panel natively instead of
      // hijacking the wheel event for hero text cycling.
      if (e.target.closest && e.target.closest(".exd-messages, .exd-chat-input")) return;
      e.preventDefault();
      handleIntent(e.deltaY > 0 ? 1 : -1);
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [onExhausted]); // eslint-disable-line

  const touchY = useRef(0);
  useEffect(() => {
    const onStart = (e) => { touchY.current = e.touches[0].clientY; };
    const onEnd   = (e) => {
      const diff = touchY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 40) return;
      handleIntent(diff > 0 ? 1 : -1);
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend",   onEnd,   { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend",   onEnd);
    };
  }, [onExhausted]); // eslint-disable-line

  return (
    <motion.div
      className="home-page"
      variants={pageVariants}
      initial="enter"
      animate="center"
      exit="exit"
    >
      <div className="hero-section">

        {/* ── Background ── */}
        <div className="hero-bg">
          {/* Poster shown instantly while video loads */}
          {!videoLoaded && (
            <div
              className="hero-thumbnail"
              style={{ backgroundColor: "#0a0804" }}
            />
          )}

          {/*
            key="hero-bg-video" — stable key prevents React from ever
            unmounting/remounting this element.

            NO src attribute here — src is set imperatively in useEffect
            so React never touches it after mount.

            preload="none" — browser won't start downloading until we
            call video.load() in useEffect.
          */}
          {/* <video
            key="hero-bg-video"
            ref={videoRef}
            className={`hero-video ${videoLoaded ? "loaded" : ""}`}
            muted
            loop
            playsInline
            preload="none"
            onCanPlay={() => {
              setVideoLoaded(true);
              if (videoRef.current) videoRef.current.playbackRate = 0.6;
            }}
          /> */}
          <div className="hero-overlay" />
        </div>

        {/* ── Content ── */}
        <div className="hero-content">
          <div className="hero-content__inner">
            <div className="hero-heading">
              {/* <h1 className="hero-heading__static">DIGITAL AGENCY</h1>
              <h1 className="hero-heading__static">HELPING STARTUPS</h1>
              <h1 className="hero-heading__row">
                <span className="hero-heading__static">AND BUSINESSES</span> */}
              <h1 className="hero-heading__static">YOU SHOULD NOT BE </h1>
              <h1 className="hero-heading__static">LIMITED BY TECHNOLOGY</h1>
              <h1 className="hero-heading__row">
                <span className="hero-heading__static">SO YOUR BUSINESS CAN</span>
                <div className="hero-preview-box">
                  {/* gif src is stable — stored in useRef */}
                  {/* <img
                    src={gifSrc}
                    alt="Abstract motion"
                    className="hero-preview-box__gif"
                    loading="lazy"
                  /> */}
                </div>
              </h1>

              <h1 className="hero-heading__animated-row">
                <OrangeText key={`${scrollIndex}-${animDir}`} dir={animDir}>
                  {SCROLL_TEXTS[scrollIndex]}
                </OrangeText>
              </h1>
            </div>
          </div>
          <div className="hero-since">Since 2011</div>
        </div>

        <div className="hero-scroll-cue">
          <span className="hero-scroll-cue__text">Scroll</span>
          <div className="hero-scroll-cue__line">
            <div className="hero-scroll-cue__dot" />
          </div>
        </div>

      </div>
    </motion.div>
  );
}

function OrangeText({ children, dir }) {
  return (
    <span className={`hero-heading__orange hero-heading__orange--${dir}`}>
      {children}
    </span>
  );
}

// import React, { useRef, useEffect, useState } from "react";
// import { motion } from "framer-motion";

// const SCROLL_TEXTS = [
//   "LAUNCH FASTER",
//   "SCALE SMARTER",
//   "WIN BIGGER",
//   "STAND OUT ONLINE",
// ];

// const pageVariants = {
//   enter:  { opacity: 0 },
//   center: { opacity: 1, transition: { duration: 0.5 } },
//   exit:   { opacity: 0, y: -40, transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] } },
// };

// export default function HeroSection({ url, onExhausted, direction }) {
//   const videoRef    = useRef(null);
//   const [videoLoaded, setVideoLoaded] = useState(false);
//   const [scrollIndex, setScrollIndex] = useState(0);
//   const [animDir,     setAnimDir]     = useState("down"); // first text bounces in from above on load

//   const idxRef      = useRef(0);
//   const debounceRef = useRef(false);

//   const handleIntent = (dir) => {
//     if (debounceRef.current) return;
//     debounceRef.current = true;
//     setTimeout(() => { debounceRef.current = false; }, 700);

//     const current = idxRef.current;
//     const next    = current + dir;

//     if (next >= 0 && next < SCROLL_TEXTS.length) {
//       idxRef.current = next;
//       setScrollIndex(next);
//       // scroll down → text falls from above (down), scroll up → rises from below (up)
//       setAnimDir(dir > 0 ? "down" : "up");
//       return;
//     }

//     if (dir > 0 && current === SCROLL_TEXTS.length - 1) {
//       onExhausted?.();
//     }
//   };

//   useEffect(() => {
//     const onWheel = (e) => {
//       e.preventDefault();
//       handleIntent(e.deltaY > 0 ? 1 : -1);
//     };
//     window.addEventListener("wheel", onWheel, { passive: false });
//     return () => window.removeEventListener("wheel", onWheel);
//   }, [onExhausted]); // eslint-disable-line

//   const touchY = useRef(0);
//   useEffect(() => {
//     const onStart = (e) => { touchY.current = e.touches[0].clientY; };
//     const onEnd   = (e) => {
//       const diff = touchY.current - e.changedTouches[0].clientY;
//       if (Math.abs(diff) < 40) return;
//       handleIntent(diff > 0 ? 1 : -1);
//     };
//     window.addEventListener("touchstart", onStart, { passive: true });
//     window.addEventListener("touchend",   onEnd,   { passive: true });
//     return () => {
//       window.removeEventListener("touchstart", onStart);
//       window.removeEventListener("touchend",   onEnd);
//     };
//   }, [onExhausted]); // eslint-disable-line

//   return (
//     <motion.div
//       className="home-page"
//       variants={pageVariants}
//       initial="enter"
//       animate="center"
//       exit="exit"
//     >
//       <div className="hero-section">
//         {/* Background */}
//         <div className="hero-bg">
//           {!videoLoaded && (
//             <div className="hero-thumbnail" style={{ backgroundColor: "#0a0804" }} />
//           )}
//           <video
//             ref={videoRef}
//             className={`hero-video ${videoLoaded ? "loaded" : ""}`}
//             autoPlay muted loop playsInline
//             onCanPlay={() => {
//               setVideoLoaded(true);
//               if (videoRef.current) videoRef.current.playbackRate = 0.6;
//             }}
//             src={`${url}videos/bg.mp4`}
//           />
//           <div className="hero-overlay" />
//         </div>

//         {/* Content */}
//         <div className="hero-content">
//           <div className="hero-content__inner">
//             <div className="hero-heading">
//               <h1 className="hero-heading__static">DIGITAL AGENCY</h1>
//               <h1 className="hero-heading__static">HELPING STARTUPS</h1>
//               <h1 className="hero-heading__row">
//                 <span className="hero-heading__static">AND BUSINESSES</span>
//                 <div className="hero-preview-box">
//                   <img
//                     src={`${url}images/new-home/hero.gif`}
//                     alt="Abstract motion"
//                     className="hero-preview-box__gif"
//                   />
//                 </div>
//               </h1>

//               {/* Animated orange text — key causes full remount → CSS animation replays */}
//               <h1 className="hero-heading__animated-row">
//                 <OrangeText key={`${scrollIndex}-${animDir}`} dir={animDir}>
//                   {SCROLL_TEXTS[scrollIndex]}
//                 </OrangeText>
//               </h1>
//             </div>
//           </div>
//           <div className="hero-since">Since 2011</div>
//         </div>

//         <div className="hero-scroll-cue">
//           <span className="hero-scroll-cue__text">Scroll</span>
//           <div className="hero-scroll-cue__line">
//             <div className="hero-scroll-cue__dot" />
//           </div>
//         </div>
//       </div>
//     </motion.div>
//   );
// }

// /**
//  * OrangeText — whole-word blur + slide reveal
//  *
//  * The entire text animates as one unit:
//  *   scroll DOWN → slides up from below while blur clears + letter-spacing tightens
//  *   scroll UP   → slides down from above with same effect
//  *
//  * key prop causes full remount → CSS animation replays every text change.
//  */
// function OrangeText({ children, dir }) {
//   return (
//     <span className={`hero-heading__orange hero-heading__orange--${dir}`}>
//       {children}
//     </span>
//   );
// }